import { useEffect, useState } from "react";
import { api, productImage, CHANNELS, socialBadge, mockupUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Share2, Loader2, Send, Check, Trash2, ExternalLink, Settings2, Save, Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const ChannelDot = ({ ch }) => {
  const c = CHANNELS.find((x) => x.key === ch);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold text-white" style={{ background: c?.color }}>{c?.short}</span>
      <span className="text-xs font-medium">{c?.label}</span>
    </span>
  );
};

export default function Social() {
  const [settings, setSettings] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ channel: "", status: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [drafts, setDrafts] = useState({});

  const loadPosts = () => {
    const qs = new URLSearchParams();
    if (filter.channel) qs.set("channel", filter.channel);
    if (filter.status) qs.set("status", filter.status);
    api.get(`/social/posts?${qs}`).then(({ data }) => setPosts(data));
  };

  useEffect(() => {
    api.get("/social/settings").then(({ data }) => setSettings(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadPosts(); }, [filter]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/social/settings", { channels: settings.channels });
      toast.success("Channel settings saved");
    } finally { setSavingSettings(false); }
  };

  const setChannel = (ch, patch) => {
    setSettings((s) => ({ ...s, channels: { ...s.channels, [ch]: { ...s.channels[ch], ...patch } } }));
  };

  const postNow = async (p) => {
    const { data } = await api.post(`/social/posts/${p.id}/post`);
    setPosts((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    toast.success(`Posted to ${p.channel} (simulated)`);
  };

  const saveCaption = async (p) => {
    const caption = drafts[p.id];
    if (caption === undefined) return;
    const { data } = await api.patch(`/social/posts/${p.id}`, { caption });
    setPosts((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    setDrafts((d) => { const n = { ...d }; delete n[p.id]; return n; });
    toast.success("Caption updated");
  };

  const remove = async (p) => {
    await api.delete(`/social/posts/${p.id}`);
    setPosts((prev) => prev.filter((x) => x.id !== p.id));
  };

  const cancelSchedule = async (p) => {
    const { data } = await api.patch(`/social/posts/${p.id}`, { status: "review" });
    setPosts((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    toast.success("Moved to review");
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-6xl mx-auto" data-testid="social-page">
      <div className="mb-5">
        <p className="overline text-blue-600">Social Distribution</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Social posts</h1>
        <p className="text-sm text-zinc-500 mt-1">AI-written posts per channel. <span className="text-amber-600 font-medium">Posting is simulated (MOCKED)</span> — connect real APIs later.</p>
      </div>

      {/* Channel settings */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-5" data-testid="channel-settings">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base font-bold tracking-tight flex items-center gap-2"><Settings2 className="w-4 h-4 text-zinc-400" /> Per-channel automation</h2>
          <button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn" className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1.5">
            {savingSettings ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {CHANNELS.map((c) => {
            const cfg = settings.channels[c.key] || { enabled: true, mode: "review" };
            return (
              <div key={c.key} className="border border-zinc-200 rounded-md p-3" data-testid={`channel-cfg-${c.key}`}>
                <div className="flex items-center justify-between">
                  <ChannelDot ch={c.key} />
                  <Switch checked={cfg.enabled} onCheckedChange={(v) => setChannel(c.key, { enabled: v })} data-testid={`enable-${c.key}`} />
                </div>
                <div className="mt-3 flex gap-1.5">
                  {["review", "auto"].map((m) => (
                    <button key={m} onClick={() => setChannel(c.key, { mode: m })} disabled={!cfg.enabled} data-testid={`mode-${c.key}-${m}`}
                      className={`flex-1 text-[11px] py-1.5 rounded-md border transition-colors disabled:opacity-40 ${cfg.mode === m ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-600"}`}>
                      {m === "review" ? "Review first" : "Auto-post"}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3">
        <select value={filter.channel} onChange={(e) => setFilter((f) => ({ ...f, channel: e.target.value }))} data-testid="filter-channel" className="bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm">
          <option value="">All channels</option>
          {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} data-testid="filter-status" className="bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="review">Needs review</option>
          <option value="scheduled">Scheduled</option>
          <option value="posted">Posted</option>
        </select>
        <span className="text-xs text-zinc-400 ml-auto">{posts.length} posts</span>
      </div>

      {posts.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-lg p-16 text-center">
          <Share2 className="w-8 h-8 text-zinc-300 mx-auto" />
          <p className="text-sm text-zinc-500 mt-3">No social posts yet. Run the Calendar Engine or generate posts from a product.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`social-post-${p.id}`} className="bg-white border border-zinc-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <ChannelDot ch={p.channel} />
                <Badge className={`text-[9px] border ${socialBadge[p.status]}`}>{p.status}</Badge>
              </div>
              <div className="flex gap-3 mt-3">
                {p.mockup_path && <img src={mockupUrl(p.mockup_path)} alt="" className="w-14 h-14 rounded-md object-cover bg-zinc-100 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-zinc-400 truncate">{p.product_title}</div>
                  <Textarea
                    value={drafts[p.id] !== undefined ? drafts[p.id] : p.caption}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    className="mt-1 text-xs min-h-[70px] resize-none"
                    data-testid={`caption-${p.id}`}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(p.hashtags || []).map((h, i) => <span key={i} className="text-[10px] text-blue-600">#{h}</span>)}
              </div>
              {p.status === "scheduled" && p.scheduled_for && (
                <div className="flex items-center gap-1.5 text-[11px] text-blue-600 mt-2" data-testid={`scheduled-${p.id}`}>
                  <Clock className="w-3 h-3" /> Auto-posts {new Date(p.scheduled_for).toLocaleString()}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
                {drafts[p.id] !== undefined && (
                  <button onClick={() => saveCaption(p)} className="h-8 px-2.5 rounded-md border border-zinc-200 hover:bg-zinc-50 text-xs font-medium flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
                )}
                {p.status !== "posted" ? (
                  <button onClick={() => postNow(p)} data-testid={`post-${p.id}`} className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" /> {p.status === "scheduled" ? "Post now" : p.mode === "auto" ? "Post now" : "Approve & post"}
                  </button>
                ) : (
                  <a href={p.permalink} target="_blank" rel="noreferrer" className="h-8 px-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-medium flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Posted <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {p.status === "scheduled" && (
                  <button onClick={() => cancelSchedule(p)} data-testid={`cancel-${p.id}`} className="h-8 px-2.5 rounded-md border border-zinc-200 hover:bg-zinc-50 text-xs font-medium text-zinc-600">Cancel</button>
                )}
                <button onClick={() => remove(p)} data-testid={`del-post-${p.id}`} className="h-8 w-8 rounded-md border border-zinc-200 hover:bg-rose-50 hover:text-rose-600 text-zinc-400 flex items-center justify-center ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
